import { RangeSetBuilder } from "@codemirror/state";
import {
  Decoration,
  EditorView,
  ViewPlugin,
  WidgetType,
  type DecorationSet,
  type ViewUpdate,
} from "@codemirror/view";

/**
 * Tabellen im Quelltext bleiben schmal (ein Leerzeichen je Zelle) – die
 * Spalten sehen im Code-Editor trotzdem exakt ausgerichtet aus. Das
 * Ausrichten passiert rein optisch über unsichtbare Füll-Widgets (feste
 * Breite in `ch`, passt bei Monospace exakt); am gespeicherten Text ändert
 * sich nichts, deshalb bleibt die Datei kurz und diff-freundlich.
 */

const SEP_CELL = /^\s*:?-+:?\s*$/;

class PadWidget extends WidgetType {
  constructor(private readonly px: number) {
    super();
  }
  eq(other: PadWidget): boolean {
    return Math.abs(other.px - this.px) < 0.5;
  }
  toDOM(): HTMLElement {
    const el = document.createElement("span");
    el.className = "cm-md-tablePad";
    el.style.display = "inline-block";
    el.style.width = this.px.toFixed(2) + "px";
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

/**
 * Textbreite in Pixeln, gemessen in der Schrift des Editors.
 *
 * Zeichen zu zählen reicht hier nicht: der Editor schreibt in einer
 * PROPORTIONALEN Schrift (siehe `--font` im CodeMirror-Theme), in der ein „i"
 * schmaler ist als ein „W". Gleich viele Zeichen heißt dort eben nicht gleich
 * breit – deshalb standen die Spalten trotz Auffüllen versetzt.
 */
const measureCanvas = document.createElement("canvas");
const measureCtx = measureCanvas.getContext("2d");

/** Misst in der Schrift des Editors; die Schrift einmal je Durchlauf statt je Zelle abfragen. */
function measurer(view: EditorView): { width: (text: string) => number; available: number } {
  const cs = getComputedStyle(view.contentDOM);
  if (measureCtx) measureCtx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const available =
    view.contentDOM.clientWidth - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0");
  return { width: (text) => (measureCtx ? measureCtx.measureText(text).width : 0), available };
}

/** Absolute Positionen unmaskierter `|` in einer Zeile. */
function pipePositions(line: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < line.length; i++) {
    if (line[i] === "|" && line[i - 1] !== "\\") out.push(i);
  }
  return out;
}

interface Cell {
  /** Position direkt nach dem getrimmten Zellentext (wo die Füllung ansetzt). */
  contentEnd: number;
  text: string;
}

/** Zellen einer Tabellenzeile: getrimmter Text + Position seines Endes. */
function cellsOf(line: string): Cell[] {
  const pipes = pipePositions(line);
  if (pipes.length < 2) return [];
  const cells: Cell[] = [];
  for (let i = 0; i < pipes.length - 1; i++) {
    const raw = line.slice(pipes[i] + 1, pipes[i + 1]);
    const leading = raw.length - raw.trimStart().length;
    const text = raw.trim();
    cells.push({ text, contentEnd: pipes[i] + 1 + leading + text.length });
  }
  return cells;
}

function isTableLine(line: string): boolean {
  return /^[ \t]*\|/.test(line);
}

function tableAlignDecos(view: EditorView): DecorationSet {
  const b = new RangeSetBuilder<Decoration>();
  const doc = view.state.doc;
  const seen = new Set<number>();
  const { width: textWidth, available } = measurer(view);

  for (const { from, to } of view.visibleRanges) {
    let lineNo = doc.lineAt(from).number;
    const lastLine = doc.lineAt(to).number;
    while (lineNo <= lastLine) {
      const line = doc.line(lineNo);
      if (seen.has(line.number) || !isTableLine(line.text)) {
        lineNo++;
        continue;
      }
      // Zusammenhängenden Tabellenblock einsammeln (mind. Kopf + Trennzeile).
      let last = line.number;
      while (last < doc.lines && isTableLine(doc.line(last + 1).text)) last++;
      const blockLines: ReturnType<typeof doc.line>[] = [];
      for (let n = line.number; n <= last; n++) {
        blockLines.push(doc.line(n));
        seen.add(n);
      }
      lineNo = last + 1;
      if (blockLines.length < 2) continue;

      const rows = blockLines.map((l) => cellsOf(l.text));
      const sepIdx = rows.findIndex((r) => r.length && r.every((c) => SEP_CELL.test(c.text)));
      if (sepIdx === -1) continue; // sieht nicht nach echter Tabelle aus

      const cols = Math.max(...rows.map((r) => r.length));
      // Spaltenbreite = breiteste Zelle in Pixeln. Die Trennzeile zählt nicht
      // mit, ihre Striche sollen sich nach dem Inhalt richten, nicht umgekehrt.
      const widths = Array.from({ length: cols }, (_, ci) =>
        Math.max(
          0,
          ...rows.filter((_, ri) => ri !== sepIdx).map((r) => (r[ci] ? textWidth(r[ci].text) : 0))
        )
      );

      // Ausgerichtet breiter als der Editor? Dann gar nicht auffüllen: die
      // Füllstücke sind feste Blöcke, die nicht umbrechen – sie ragten über den
      // Rand (seitliches Scrollen) und zerrissen umbrochene Zeilen zu einem
      // unlesbaren Flickenteppich. Außerhalb des Cursors zeigt der Editor die
      // Tabelle ohnehin gerendert (tableWidget.ts).
      const alignedWidth = Math.max(
        ...rows.map((row, ri) => {
          const pads = row.reduce((sum, cell, ci) => sum + Math.max(0, widths[ci] - textWidth(cell.text)), 0);
          return textWidth(blockLines[ri].text) + pads;
        })
      );
      if (alignedWidth > available - 2) continue;

      rows.forEach((row, ri) => {
        // `cellsOf` liefert Spalten relativ zur Zeile (Textanalyse) – hier
        // erst auf absolute Dokumentposition umrechnen. Ohne `line.from` ist
        // das nur für die allererste Zeile des Dokuments zufällig richtig;
        // ab der zweiten Zeile bekäme RangeSetBuilder fallende Positionen und
        // wirft (das war der Absturz "Invalid position …").
        const lineFrom = blockLines[ri].from;
        row.forEach((cell, ci) => {
          const pad = widths[ci] - textWidth(cell.text);
          if (pad > 0.5) {
            const at = lineFrom + cell.contentEnd;
            b.add(at, at, Decoration.widget({ widget: new PadWidget(pad), side: -1 }));
          }
        });
      });
    }
  }
  return b.finish();
}

function safeDecos(view: EditorView): DecorationSet {
  try {
    return tableAlignDecos(view);
  } catch {
    // Rein optisches Feature – im Zweifel lieber unausgerichtet als den
    // ganzen Editor mit hochreißen.
    return Decoration.none;
  }
}

export const tableAlignPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = safeDecos(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged || u.geometryChanged) this.decorations = safeDecos(u.view);
    }
  },
  { decorations: (v) => v.decorations }
);
